import React from "react"
import styled from "@emotion/styled"
import * as layouts from "layouts"

export const PageContainer = styled(layouts.Flex)`
	flex-direction: column;
	height: 100%;
	background: #f5f5f5;
`

export const PageHeader = styled(layouts.Flex)`
	flex-direction: column;
	padding: 30px 40px 20px;
	background: white;
	border-bottom: 1px solid #e0e0e0;
`

export const PageTitle = styled.h1`
	font-size: 28px;
	font-weight: 600;
	color: #212121;
	margin: 0 0 8px 0;
`

export const PageDescription = styled.p`
	font-size: 14px;
	color: #757575;
	margin: 0;
	line-height: 1.5;
`

export const ContentArea = styled(layouts.Flex)`
	flex: 1;
	flex-direction: column;
	overflow-y: auto;
	padding: 20px 40px;
`

export const GridContainer = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
	gap: 20px;
	padding: 20px 0;
`

export const Card = styled.div`
	position: relative;
	background: white;
	border-radius: 10px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
	overflow: hidden;
	transition: transform 0.2s, box-shadow 0.2s;
	cursor: pointer;

	&:hover {
		transform: translateY(-2px);
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
	}
`

export const CardImage = styled.div<{ src: string }>`
	width: 100%;
	height: 200px;
	background-image: url(${(props) => props.src});
	background-size: cover;
	background-position: center;
	background-color: #f5f5f5;
`

export const CardContent = styled.div`
	padding: 15px;
`

export const CardTitle = styled.div`
	font-size: 14px;
	font-weight: 600;
	color: #212121;
	margin-bottom: 8px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`

export const CardMeta = styled.div`
	font-size: 12px;
	color: #757575;
	margin-bottom: 4px;
`

export const Badge = styled.span<{ $status?: string }>`
	display: inline-block;
	padding: 4px 8px;
	border-radius: 4px;
	font-size: 11px;
	font-weight: 600;
	text-transform: uppercase;
	background: ${(props) =>
		props.$status === "approved"
			? "#e8f4f8"
			: props.$status === "rejected"
			? "#fdeef0"
			: props.$status === "pending"
			? "#fff3e0"
			: "#f5f5f5"};
	color: ${(props) =>
		props.$status === "approved"
			? "#0277bd"
			: props.$status === "rejected"
			? "#c62828"
			: props.$status === "pending"
			? "#e65100"
			: "#757575"};
`

export const ActionButton = styled.button<{ $variant?: "primary" | "secondary" | "danger" }>`
	padding: 8px 16px;
	border: none;
	border-radius: 5px;
	font-size: 13px;
	font-weight: 600;
	cursor: pointer;
	transition: background-color 0.2s;
	background: ${(props) =>
		props.$variant === "primary"
			? "#2196F3"
			: props.$variant === "danger"
			? "#f44336"
			: props.$variant === "secondary"
			? "#757575"
			: "#e0e0e0"};
	color: ${(props) => (props.$variant === "primary" || props.$variant === "danger" ? "white" : "#212121")};

	&:hover {
		background: ${(props) =>
			props.$variant === "primary"
				? "#1976D2"
				: props.$variant === "danger"
				? "#d32f2f"
				: props.$variant === "secondary"
				? "#616161"
				: "#bdbdbd"};
	}

	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
`

export const ButtonGroup = styled(layouts.Flex)`
	gap: 10px;
	margin-top: 10px;
`

export const FilterBar = styled(layouts.Flex)`
	gap: 15px;
	padding: 15px 20px;
	background: white;
	border-radius: 8px;
	margin-bottom: 20px;
	align-items: center;
`

export const FilterLabel = styled.label`
	font-size: 13px;
	font-weight: 600;
	color: #616161;
	margin-right: 8px;
`

export const Select = styled.select`
	padding: 8px 12px;
	border: 1px solid #e0e0e0;
	border-radius: 5px;
	font-size: 13px;
	color: #212121;
	background: white;
	cursor: pointer;

	&:focus {
		outline: none;
		border-color: #2196f3;
	}
`

export const Input = styled.input`
	padding: 8px 12px;
	border: 1px solid #e0e0e0;
	border-radius: 5px;
	font-size: 13px;
	color: #212121;
	background: white;

	&:focus {
		outline: none;
		border-color: #2196f3;
	}
`

const StyledCheckbox = styled.input`
	width: 18px;
	height: 18px;
	cursor: pointer;
	margin: 0;
`

export const Checkbox = (props: React.InputHTMLAttributes<HTMLInputElement>) => (
	<StyledCheckbox type="checkbox" {...props} />
)

export const BottomActionBar = styled(layouts.Flex)`
	position: sticky;
	bottom: 0;
	left: 0;
	right: 0;
	padding: 15px 40px;
	background: white;
	border-top: 1px solid #e0e0e0;
	align-items: center;
	justify-content: space-between;
	z-index: 100;
`

export const ActionBarText = styled.div`
	font-size: 14px;
	color: #616161;
`

export const UploadArea = styled.div<{ $isDragActive: boolean }>`
	border: 2px dashed ${(props) => (props.$isDragActive ? "#2196F3" : "#bdbdbd")};
	border-radius: 10px;
	padding: 60px 40px;
	text-align: center;
	background: ${(props) => (props.$isDragActive ? "#e3f2fd" : "white")};
	transition: all 0.2s;
	cursor: pointer;

	&:hover {
		border-color: #2196f3;
		background: #e3f2fd;
	}
`

export const UploadIcon = styled.div`
	font-size: 48px;
	color: #bdbdbd;
	margin-bottom: 20px;
`

export const UploadText = styled.div`
	font-size: 16px;
	font-weight: 600;
	color: #212121;
	margin-bottom: 8px;
`

export const UploadHint = styled.div`
	font-size: 13px;
	color: #757575;
`

export const ProgressBar = styled.div<{ progress: number }>`
	width: 100%;
	height: 8px;
	background: #e0e0e0;
	border-radius: 4px;
	overflow: hidden;
	margin-top: 20px;

	&::after {
		content: "";
		display: block;
		width: ${(props) => props.progress}%;
		height: 100%;
		background: #2196f3;
		transition: width 0.3s;
	}
`

export const EmptyState = styled(layouts.Flex)`
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 60px 40px;
	text-align: center;
`

export const EmptyStateIcon = styled.div`
	font-size: 64px;
	color: #e0e0e0;
	margin-bottom: 20px;
`

export const EmptyStateTitle = styled.div`
	font-size: 18px;
	font-weight: 600;
	color: #616161;
	margin-bottom: 8px;
`

export const EmptyStateText = styled.div`
	font-size: 14px;
	color: #9e9e9e;
	max-width: 400px;
`

export const Modal = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background: rgba(0, 0, 0, 0.6);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 1000;
`

export const ModalContent = styled.div`
	background: white;
	border-radius: 10px;
	padding: 30px;
	max-width: 600px;
	width: 90%;
	max-height: 80vh;
	overflow-y: auto;
`

export const ModalTitle = styled.h2`
	font-size: 22px;
	font-weight: 600;
	color: #212121;
	margin: 0 0 20px 0;
`

export const FormGroup = styled.div`
	margin-bottom: 20px;
`

export const FormLabel = styled.label`
	display: block;
	font-size: 13px;
	font-weight: 600;
	color: #616161;
	margin-bottom: 8px;
`

export const HelpText = styled.div`
	font-size: 12px;
	color: #9e9e9e;
	margin-top: 4px;
`

export const ErrorMessage = styled.div`
	padding: 12px 16px;
	background: #ffebee;
	border-left: 4px solid #f44336;
	border-radius: 4px;
	color: #c62828;
	font-size: 13px;
	margin-bottom: 20px;
`

export const SuccessMessage = styled.div`
	padding: 12px 16px;
	background: #e8f5e9;
	border-left: 4px solid #4caf50;
	border-radius: 4px;
	color: #2e7d32;
	font-size: 13px;
	margin-bottom: 20px;
`
